/* Pictures for the booking assistant.

   Brian, 29 September 2026: "I want to be able to post pictures to the feed so
   it can just book the transaction." A screenshot of the day's distribution
   sheet is pasted, dropped or picked on the Review screen, shrunk here to what
   the model actually reads, and sent with the question as base64.

   Every picture is re-drawn on a canvas before it goes, whatever it arrived
   as: that is what guarantees the type we name is the type the bytes are (the
   server refuses a PNG label on JPEG bytes, and a browser's `file.type` is
   only the file's extension), and it is where a retina screenshot comes down
   to 2,576 pixels on the long edge, the most the model reads. PNG first,
   because a table of figures in JPEG smudges the digits; JPEG only when the
   PNG will not fit under the server's per-picture limit. */

export const PICTURE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
export const MAX_EDGE = 2576;
export const MAX_PIXELS = 3750000;
// Under the server's 3.75 MB, which is the API's 5 MB of base64.
export const MAX_BYTES = 3500000;
export const MAX_PER_TURN = 4;

/* The size a picture is drawn at: its own, or shrunk to fit both limits. */
export function fitWithin(width, height, maxEdge = MAX_EDGE, maxPixels = MAX_PIXELS) {
  if (!(width > 0) || !(height > 0)) return { width: 0, height: 0, scale: 0 };
  const scale = Math.min(1, maxEdge / Math.max(width, height), Math.sqrt(maxPixels / (width * height)));
  return {
    width: Math.max(1, Math.floor(width * scale)),
    height: Math.max(1, Math.floor(height * scale)),
    scale,
  };
}

/* The picture files in a paste or a drop, in order. Anything else is left
   alone, so pasting text into the box still pastes text. */
export function pictureFiles(transfer) {
  if (!transfer) return [];
  const out = [];
  const items = Array.from(transfer.items || []);
  if (items.length) {
    items.forEach(it => {
      if (it.kind === 'file' && (it.type || '').startsWith('image/')) {
        const f = it.getAsFile();
        if (f) out.push(f);
      }
    });
    return out;
  }
  return Array.from(transfer.files || []).filter(f => (f.type || '').startsWith('image/'));
}

/* A name that says what the picture was. A pasted screenshot arrives as
   "image.png" whatever it shows, which tells the model and the review note
   nothing. */
export function pictureName(file, n) {
  const raw = (file && file.name) || '';
  if (!raw || /^image\.(png|jpe?g|gif|webp)$/i.test(raw)) return `Pasted screenshot ${n}`;
  return raw.slice(0, 120);
}

/* The model id the server reports, as a person reads it ("Claude Opus 5.5"). */
export function modelLabel(id) {
  const m = /^claude-(opus|sonnet|haiku|fable|mythos)-(\d+)(?:-(\d+))?$/.exec(id || '');
  if (!m) return id || '';
  const family = m[1].charAt(0).toUpperCase() + m[1].slice(1);
  return `Claude ${family} ${m[2]}${m[3] ? '.' + m[3] : ''}`;
}

const loadImage = (url) => new Promise((resolve, reject) => {
  const im = new Image();
  im.onload = () => resolve(im);
  im.onerror = () => reject(new Error('not a picture the browser can read'));
  im.src = url;
});

const toBlob = (canvas, type, quality) => new Promise(resolve => canvas.toBlob(resolve, type, quality));

const readAsDataURL = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = () => reject(r.error || new Error('could not read the picture'));
  r.readAsDataURL(blob);
});

/* One file, ready to send: { name, media_type, data (base64), preview (an
   object URL for the thumbnail — a data URL will not open in a new tab),
   width, height }. Throws with a sentence to show. */
export async function preparePicture(file, n = 1) {
  const name = pictureName(file, n);
  if (!file || !(file.type || '').startsWith('image/')) {
    throw new Error(`${name} is not a picture.`);
  }
  const url = URL.createObjectURL(file);
  try {
    let im;
    try {
      im = await loadImage(url);
    } catch (e) {
      throw new Error(`${name}: ${e.message}.`);
    }
    const size = fitWithin(im.naturalWidth, im.naturalHeight);
    if (!size.width) throw new Error(`${name} has no size.`);
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext('2d');
    // White under anything transparent, so a JPEG fallback does not turn it black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size.width, size.height);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(im, 0, 0, size.width, size.height);
    let type = 'image/png';
    let blob = await toBlob(canvas, type);
    if (!blob || blob.size > MAX_BYTES) {
      type = 'image/jpeg';
      blob = await toBlob(canvas, type, 0.92);
    }
    if (!blob || blob.size > MAX_BYTES) throw new Error(`${name} is too large even after shrinking it.`);
    const dataUrl = await readAsDataURL(blob);
    return {
      name, media_type: type, data: dataUrl.slice(dataUrl.indexOf(',') + 1),
      preview: URL.createObjectURL(blob), width: size.width, height: size.height,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/* The pictures last sent from any row, for as long as the page is open. One
   distribution sheet pays half a dozen people, so the next row's Review screen
   offers the same picture instead of asking for it again. Kept in memory only:
   nothing about a picture is stored anywhere. */
let lastSent = [];
export const rememberPictures = (pictures) => { lastSent = (pictures || []).slice(0, MAX_PER_TURN); };
export const recentPictures = () => lastSent;
